import { useMemo, useState } from 'react';
import { Card, Typography, Tag, Input, Button, Space, Tooltip, Avatar, Select, Badge, Popover, Popconfirm, theme, message, Slider } from '../../ui';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Phone, MessageCircle, Plus, Pencil, Eye, User,
  Filter, Eraser, MapPin, ShieldCheck, ArrowLeftRight,
  Ban, CheckCircle2, Merge, Upload, Download,
} from 'lucide-react';
import { request } from '../../api/client';
import { usePermissionChecker } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { DataTable, DataTableColumn } from '../../components/DataTable';
import { BulkImportModal } from '../../components/BulkImportModal';
import { TemplateButton } from '../../components/TemplateButton';
import { downloadCsv } from '../../lib/downloadCsv';
import { FarmerFormModal } from './FarmerFormModal';
import { FarmerDetailDrawer } from './FarmerDetailDrawer';
import { ChangeTerritoryModal } from '../territory/ChangeTerritoryModal';
import { MergeCustomerModal } from '../territory/MergeCustomerModal';
import { displayMobile, waNumber } from '../../lib/validators';
import { formatIstDate } from '../../lib/time';
import { STATE_NAME } from '../../constants/indianStates';

const STATE_LABEL = STATE_NAME;

// Google Maps link for a farmer: precise pin when GPS exists, else a "village, State" search.
const farmerMapUrl = (r: any): string | null => {
  const lat = Number(r.latitude), lng = Number(r.longitude);
  if (r.latitude != null && r.longitude != null && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  const place = [r.city, r.stateCode ? (STATE_LABEL.get(r.stateCode) ?? r.stateCode) : ''].filter(Boolean).join(', ');
  return place ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}` : null;
};
const LANG_LABEL: Record<string, string> = {
  en: 'English', hi: 'Hindi', mr: 'Marathi', bn: 'Bengali', or: 'Odia',
  ta: 'Tamil', te: 'Telugu', gu: 'Gujarati', kn: 'Kannada', pa: 'Punjabi',
};
const opt = (arr: any[]) => (arr ?? []).map((o: any) => ({ value: o.value, label: `${o.label}${o.count != null ? ` (${o.count})` : ''}` }));

export function FarmersListPage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<any | undefined>();
  const [reassigning, setReassigning] = useState<any | undefined>();
  const [viewing, setViewing] = useState<any | undefined>();
  const hasPerm = usePermissionChecker();
  const canCreate = hasPerm('FARMER.CREATE');
  const canImport = hasPerm('RETAILER.IMPORT');
  const canExport = hasPerm('FARMER.VIEW');
  const canEdit = hasPerm('FARMER.EDIT');
  const canTransfer = hasPerm('TERRITORY.TRANSFER');
  const canMerge = hasPerm('TERRITORY.SPLIT_MERGE');
  const canStatus = hasPerm('FARMER.STATUS'); // activate/deactivate — Admin / Super Admin only
  const [mergingDup, setMergingDup] = useState<any | undefined>();
  const qc = useQueryClient();
  const statusMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      request({ url: `/farmers/${id}/status`, method: 'PATCH', data: { active } }),
    onSuccess: (_d, v) => {
      message.success(v.active ? 'Farmer activated' : 'Farmer deactivated');
      qc.invalidateQueries({ queryKey: ['farmers'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed to change status'),
  });

  // Import owner-assignment: a fallback SP for farmers with no owner (by mobile or territory), plus a
  // one-time "assign unowned by territory" backfill for farmers already imported without an owner.
  const [leftoverSp, setLeftoverSp] = useState<string | undefined>();
  const { data: spUsers } = useQuery({
    queryKey: ['sp-list-all'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'SALES_PERSON', pageSize: 200 } }),
    enabled: importOpen && canImport,
  });
  const spOptions = ((spUsers?.data ?? []) as any[])
    .filter((u) => u.status === 'active')
    .map((u) => ({ value: String(u.id), label: `${u.fullName}${u.mobile ? ` · ${displayMobile(u.mobile)}` : ''}` }));
  const backfill = useMutation({
    mutationFn: () => request<{ assigned: number; stillUnowned: number }>({ url: '/farmers/backfill-owners', method: 'POST' }),
    onSuccess: (res: any) => {
      message.success(`Assigned ${res?.assigned ?? 0} unowned farmer(s) by territory${res?.stillUnowned ? ` · ${res.stillUnowned} still unassigned` : ''}`);
      qc.invalidateQueries({ queryKey: ['farmers'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Backfill failed'),
  });

  // ---- Filters (server-side) ----
  const [stateF, setStateF] = useState<string[]>([]);
  const [cropsF, setCropsF] = useState<string[]>([]);
  const [viaF, setViaF] = useState<string[]>([]);
  const [langF, setLangF] = useState<string[]>([]);
  const [genderF, setGenderF] = useState<string[]>([]);
  const [hasKyc, setHasKyc] = useState(false);
  const [land, setLand] = useState<[number, number] | null>(null);

  const params = useMemo(() => {
    const p: Record<string, any> = { page, pageSize };
    if (search.trim()) p.search = search.trim();
    if (stateF.length) p.state = stateF.join(',');
    if (cropsF.length) p.crops = cropsF.join(',');
    if (viaF.length) p.registeredVia = viaF.join(',');
    if (langF.length) p.languagePref = langF.join(',');
    if (genderF.length) p.gender = genderF.join(',');
    if (hasKyc) p.hasKyc = 'true';
    if (land) { p.landMin = land[0]; p.landMax = land[1]; }
    return p;
  }, [page, pageSize, search, stateF, cropsF, viaF, langF, genderF, hasKyc, land]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['farmers', params],
    queryFn: () => request<any>({ url: '/farmers', params }),
    placeholderData: keepPreviousData,
  });
  const { data: facets } = useQuery({
    queryKey: ['farmers', 'filter-options'],
    queryFn: () => request<any>({ url: '/farmers/filter-options' }),
  });

  const stateOpts = useMemo(() => (facets?.states ?? []).map((s: any) => ({ value: s.value, label: `${STATE_LABEL.get(s.value) ?? s.value} (${s.count})` })), [facets]);
  const landBounds: [number, number] = [Math.floor(facets?.landMin ?? 0), Math.ceil(facets?.landMax ?? 0)];
  const hasLand = landBounds[1] > landBounds[0];

  const activeFilters = stateF.length + cropsF.length + viaF.length + langF.length + genderF.length
    + (hasKyc ? 1 : 0) + (land ? 1 : 0) + (search ? 1 : 0);
  const clearFilters = () => {
    setSearchInput(''); setStateF([]); setCropsF([]); setViaF([]); setLangF([]); setGenderF([]);
    setHasKyc(false); setLand(null); setPage(1);
  };

  const columns: DataTableColumn<any>[] = [
    { key: 'uuid', title: 'ID', dataIndex: 'uuid', width: 110, defaultVisible: false,
      render: (v: string) => <code style={{ fontSize: 11 }}>{v?.slice(0, 8)}…</code> },
    { key: 'photoUrl', title: '', dataIndex: 'photoUrl', width: 50, alwaysVisible: true,
      render: (v: string) => <Avatar size={32} src={v} fallback={<User size={16} />} /> },
    { key: 'fullName', title: 'Name', dataIndex: 'fullName', sortable: true, alwaysVisible: true, width: 220, ellipsis: true },
    {
      key: 'mobile', title: 'Mobile', dataIndex: 'mobile', sortable: true, alwaysVisible: true, width: 140,
      render: (v: string) => v && <a href={`tel:${displayMobile(v)}`} onClick={(e) => e.stopPropagation()}>{displayMobile(v)}</a>,
    },
    {
      key: 'whatsappNumber', title: 'WhatsApp', dataIndex: 'whatsappNumber', width: 140, defaultVisible: false,
      render: (v: string) => v && <a href={`https://wa.me/${waNumber(v)}`} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>{displayMobile(v)}</a>,
    },
    { key: 'gender', title: 'Gender', dataIndex: 'gender', width: 90, defaultVisible: false },
    { key: 'languagePref', title: 'Language', dataIndex: 'languagePref', sortable: true, width: 110,
      render: (v: string) => v && <Tag>{LANG_LABEL[v] ?? v.toUpperCase()}</Tag> },
    { key: 'location', title: 'Location', dataIndex: 'city', width: 200, ellipsis: true, alwaysVisible: true,
      render: (city: string, r: any) => {
        const state = r.stateCode ? (STATE_LABEL.get(r.stateCode) ?? r.stateCode) : null;
        const txt = [city, state].filter(Boolean).join(' · ');
        return txt || <span style={{ color: '#bbb' }}>—</span>;
      } },
    { key: 'pincode', title: 'Pincode', dataIndex: 'pincode', width: 90, defaultVisible: false },
    {
      key: 'crops', title: 'Crops', dataIndex: 'crops', ellipsis: true, width: 240,
      render: (crops: string[]) => (crops ?? []).map((c) => <Tag tone="green" key={c}>{c}</Tag>),
    },
    { key: 'landAcres', title: 'Land (ac)', dataIndex: 'landAcres', sortable: true, align: 'right', width: 90, defaultVisible: false,
      render: (v: any) => (v != null ? Number(v) : '') },
    { key: 'registeredVia', title: 'Source', dataIndex: 'registeredVia', sortable: true, width: 100,
      render: (v: string) => v && <Tag>{v}</Tag> },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 100,
      render: (s: string) => <Tag tone={s === 'INACTIVE' ? 'red' : 'green'}>{s || 'ACTIVE'}</Tag> },
    {
      key: 'gps', title: 'GPS', width: 60, defaultVisible: false,
      render: (_: any, r: any) => (r.latitude && r.longitude ? <Tag tone="blue">✓</Tag> : <Tag>—</Tag>),
    },
    {
      key: 'kyc', title: 'KYC', width: 60, defaultVisible: false,
      render: (_: any, r: any) => (r.kycDocUrl ? <Tag tone="green">Yes</Tag> : <Tag>No</Tag>),
    },
    { key: 'createdAt', title: 'Registered', dataIndex: 'createdAt', sortable: true, width: 150,
      render: (v: string) => formatIstDate(v) },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 150, fixed: 'right',
      render: (_: any, r: any) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Tooltip label="View details"><Button size="sm" variant="text" icon={<Eye size={16} />} onClick={() => setViewing(r)} aria-label="View details" /></Tooltip>
          {farmerMapUrl(r) && (
            <Tooltip label="Location on Google Maps"><Button size="sm" variant="text" icon={<MapPin size={16} />} href={farmerMapUrl(r)!} target="_blank" rel="noopener" aria-label="Location on Google Maps" /></Tooltip>
          )}
          {canEdit && (
            <Tooltip label="Edit"><Button size="sm" variant="text" icon={<Pencil size={16} />} onClick={() => { setEditing(r); setFormOpen(true); }} aria-label="Edit" /></Tooltip>
          )}
          {canStatus && (
            <Popconfirm
              title={r.status === 'INACTIVE' ? 'Activate this farmer?' : 'Deactivate this farmer?'}
              description={r.status === 'INACTIVE' ? 'They will be marked ACTIVE.' : 'They will be marked INACTIVE.'}
              okText={r.status === 'INACTIVE' ? 'Activate' : 'Deactivate'}
              okButtonProps={{ danger: r.status !== 'INACTIVE', loading: statusMut.isPending }}
              onConfirm={() => statusMut.mutate({ id: String(r.id), active: r.status === 'INACTIVE' })}
            >
              <Tooltip label={r.status === 'INACTIVE' ? 'Activate' : 'Deactivate'}>
                <Button size="sm" variant={r.status !== 'INACTIVE' ? 'danger' : 'default'}
                  aria-label={r.status === 'INACTIVE' ? 'Activate' : 'Deactivate'}
                  icon={r.status === 'INACTIVE' ? <CheckCircle2 size={16} /> : <Ban size={16} />} />
              </Tooltip>
            </Popconfirm>
          )}
          {canTransfer && (
            <Tooltip label="Change territory"><Button size="sm" variant="text" icon={<ArrowLeftRight size={16} />} onClick={() => setReassigning(r)} aria-label="Change territory" /></Tooltip>
          )}
          {canMerge && (
            <Tooltip label="Merge duplicate"><Button size="sm" variant="text" icon={<Merge size={16} />} onClick={() => setMergingDup(r)} aria-label="Merge duplicate" /></Tooltip>
          )}
          <Tooltip label="Call"><Button size="sm" variant="text" icon={<Phone size={16} />} href={`tel:${displayMobile(r.mobile)}`} aria-label="Call" /></Tooltip>
          <Tooltip label="WhatsApp">
            <Button size="sm" variant="text" icon={<MessageCircle size={16} />}
              href={`https://wa.me/${waNumber(r.whatsappNumber ?? r.mobile)}`} target="_blank" rel="noopener" aria-label="WhatsApp" />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Farmers / Customers</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Space size="small">
            {canCreate && (
              <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(undefined); setFormOpen(true); }}>
                Register farmer
              </Button>
            )}
            {canImport && (
              <Button size="sm" icon={<Upload size={16} />} onClick={() => setImportOpen(true)}>
                Import CSV
              </Button>
            )}
            {canExport && (
              <Button size="sm" icon={<Download size={16} />} onClick={() => downloadCsv('/farmers/export.csv', 'farmers.csv')}>
                Export CSV
              </Button>
            )}
            {canImport && <TemplateButton kind="farmers" size="small" />}
            {canImport && (
              <Popconfirm
                title="Assign unowned farmers?"
                description="Give every farmer that has no sales person the primary SP of its territory."
                okText="Assign" onConfirm={() => backfill.mutate()}
              >
                <Button size="sm" icon={<ShieldCheck size={16} />} loading={backfill.isPending}>Assign owners</Button>
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>
      <Card>
        <Space wrap size={[8, 8]} style={{ marginBottom: 12, width: '100%' }}>
          <Input
            placeholder="Search name, mobile, WhatsApp" prefix={<Filter size={16} style={{ color: token.colorTextTertiary }} />}
            className="w-60" value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
          />
          <Select mode="multiple" allowClear placeholder="State" style={{ minWidth: 150 }} maxTagCount={1}
            value={stateF} onChange={(v) => { setStateF(v); setPage(1); }} options={stateOpts} />
          <Select mode="multiple" allowClear showSearch placeholder="Crops" style={{ minWidth: 160 }} maxTagCount={1}
            value={cropsF} onChange={(v) => { setCropsF(v); setPage(1); }} options={opt(facets?.crops)} />
          <Select mode="multiple" allowClear placeholder="Source" style={{ minWidth: 130 }} maxTagCount={1}
            value={viaF} onChange={(v) => { setViaF(v); setPage(1); }} options={opt(facets?.registeredVia)} />
          <Select mode="multiple" allowClear placeholder="Language" style={{ minWidth: 130 }} maxTagCount={1}
            value={langF} onChange={(v) => { setLangF(v); setPage(1); }} options={opt(facets?.languages)} />
          <Select mode="multiple" allowClear placeholder="Gender" style={{ minWidth: 120 }} maxTagCount={1}
            value={genderF} onChange={(v) => { setGenderF(v); setPage(1); }} options={opt(facets?.genders)} />
          {hasLand && (
            <Popover trigger="click" title="Land (acres)" content={
              <div style={{ width: 220 }}>
                <Slider range min={landBounds[0]} max={landBounds[1]} value={land ?? landBounds} onChange={(v) => { setLand(v as [number, number]); setPage(1); }} />
                {land && <Button size="sm" variant="text" style={{ padding: 0 }} onClick={() => { setLand(null); setPage(1); }}>Reset</Button>}
              </div>
            }>
              <Button>{land ? `${land[0]}–${land[1]} ac` : 'Land'}</Button>
            </Popover>
          )}
          <Tooltip label="Has KYC document">
            <Button icon={<ShieldCheck size={16} />} variant={hasKyc ? 'primary' : 'default'} onClick={() => { setHasKyc((v) => !v); setPage(1); }}>KYC</Button>
          </Tooltip>
          {activeFilters > 0 && (
            <Badge count={activeFilters} size="small"><Button icon={<Eraser size={16} />} onClick={clearFilters}>Clear</Button></Badge>
          )}
        </Space>

        <DataTable
          storageKey="farmers-list"
          rowKey="id"
          dataSource={data?.data ?? []}
          loading={isLoading || isFetching}
          columns={columns}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total: data?.meta?.total ?? 0,
            showSizeChanger: true,
            showTotal: (t) => `${t.toLocaleString('en-IN')} farmers`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>
      <FarmerFormModal open={formOpen} record={editing} onClose={() => { setFormOpen(false); setEditing(undefined); }} />
      <FarmerDetailDrawer farmer={viewing} open={!!viewing} onClose={() => setViewing(undefined)} />
      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['farmers'] })}
        kind="farmers"
        entityPlural="farmers"
        allowUpsert
        columns="fullName, mobile, whatsappNumber, gender, dob, languagePref, territoryCode, salesPersonMobile, city, address, stateCode, pincode, latitude, longitude, crops, landAcres"
        note={<>Required: <b>fullName</b> and <b>mobile</b> (unique). Everything else is optional. <b>salesPersonMobile</b> = the sales person’s login mobile (blank → auto-assign by territory); <b>territoryCode</b> = a territory’s code (e.g. MH-JALGAON); <b>stateCode</b> = 2-digit GST code (e.g. 27); <b>dob</b> = YYYY-MM-DD; <b>crops</b> = separate with “;”. </>}
        extraControl={
          <div>
            <div style={{ fontSize: 12.5, marginBottom: 4, color: token.colorTextSecondary }}>
              Assign farmers with no sales person (by mobile or territory) to:
            </div>
            <Select
              allowClear showSearch optionFilterProp="label" className="w-full"
              placeholder="Optional — leave blank to import them unassigned"
              value={leftoverSp} onChange={setLeftoverSp} options={spOptions}
            />
          </div>
        }
        extraParams={() => (leftoverSp ? { leftoverSalesPersonId: leftoverSp } : {})}
      />
      {canTransfer && (
        <ChangeTerritoryModal open={!!reassigning} onClose={() => setReassigning(undefined)} customerType="FARMER" customer={reassigning ?? {}} />
      )}
      {canMerge && (
        <MergeCustomerModal open={!!mergingDup} onClose={() => setMergingDup(undefined)} customerType="FARMER" duplicate={mergingDup ?? {}} />
      )}
    </div>
  );
}
